import {
  buildImageEvidenceTilesFromPixelRagSegments,
  buildImageSourceEvidence,
  type GeneratedUiVerificationSurface,
  type ImageSourceEvidence,
  type PixelRagVisualSegment,
} from "@cogo/headless-core/image-evidence-verification";
import type {
  AppSpecFull,
  StudioImageEvidenceSourceInput,
} from "@/services/block-api-service";

export interface ImageEvidenceVerificationShadowOptions {
  enabled?: boolean;
  source?: string;
  imageSourceEvidence?: ImageSourceEvidence;
  imageEvidenceSource?: StudioImageEvidenceSourceInput;
  pixelRagVisualSegments?: PixelRagVisualSegment[];
  inferredHiddenTruths?: string[];
}

function readEnvFlag(name: string): boolean {
  const viteEnv = (
    import.meta as unknown as { env?: Record<string, string | undefined> }
  ).env;
  const processEnv = (
    globalThis as typeof globalThis & {
      process?: { env?: Record<string, string | undefined> };
    }
  ).process?.env;
  const value = viteEnv?.[name] ?? processEnv?.[name];
  return value === "1" || value === "true" || value === "yes" || value === "on";
}

export function isImageEvidenceVerificationShadowEnabled(): boolean {
  return (
    readEnvFlag("VITE_COGO_IMAGE_EVIDENCE_VERIFICATION") ||
    readEnvFlag("COGO_IMAGE_EVIDENCE_VERIFICATION")
  );
}

function stableDigest(value: unknown): string {
  const text = JSON.stringify(value);
  let hash = 0;
  for (let index = 0; index < text.length; index += 1) {
    hash = (hash * 31 + text.charCodeAt(index)) >>> 0;
  }
  return `shadow-${hash.toString(16).padStart(8, "0")}`;
}

function imageEvidenceSourceFromOptions(
  appSpec: AppSpecFull,
  options: ImageEvidenceVerificationShadowOptions,
): ImageSourceEvidence | undefined {
  if (options.imageSourceEvidence) return options.imageSourceEvidence;
  if (appSpec.imageSourceEvidence) return appSpec.imageSourceEvidence;

  const segments =
    options.pixelRagVisualSegments ?? appSpec.pixelRagVisualSegments;
  if (!segments?.length) return undefined;

  const source =
    options.imageEvidenceSource ?? appSpec.imageEvidenceSource ?? {};
  const sourceId =
    source.sourceId ?? `${appSpec.appId ?? appSpec.name}:image-source`;
  const unresolvedAmbiguity = [
    ...(source.unresolvedAmbiguity ?? []),
    ...(source.sourceDigest
      ? []
      : [
          "sourceDigest was derived from PixelRAG segment metadata in shadow mode",
        ]),
  ];

  return buildImageSourceEvidence({
    sourceId,
    sourceKind: source.sourceKind ?? "app-screenshot",
    structuredSourceAvailable: source.structuredSourceAvailable ?? false,
    tiles: buildImageEvidenceTilesFromPixelRagSegments(segments),
    sourceDigest: source.sourceDigest ?? stableDigest({ sourceId, segments }),
    capturedAt: source.capturedAt ?? new Date().toISOString(),
    captureTool: source.captureTool,
    viewport: source.viewport,
    deviceClass: source.deviceClass,
    unresolvedAmbiguity,
  });
}

function blockVerificationSurface(
  appSpec: AppSpecFull,
  block: AppSpecFull["blocks"][number],
  index: number,
  sourceEvidence: ImageSourceEvidence,
  hiddenTruths: string[],
): GeneratedUiVerificationSurface {
  return {
    id: `${appSpec.appId ?? appSpec.name}:block:${block.name || index}`,
    provenanceTier: "image-derived-candidate",
    changedFields: ["content", "layout", "style"],
    schemaValid: true,
    sourceEvidence,
    usesRasterSourceAsOutput: false,
    ...(hiddenTruths.length > 0 ? { inferredHiddenTruths: hiddenTruths } : {}),
  };
}

export function withImageEvidenceVerificationShadow<T extends AppSpecFull>(
  appSpec: T,
  options: ImageEvidenceVerificationShadowOptions = {},
): T {
  const sourceEvidence = imageEvidenceSourceFromOptions(appSpec, options);
  const hasImageInput = Boolean(sourceEvidence);
  const explicitEnabled =
    options.enabled ?? appSpec.imageEvidenceVerificationEnabled;
  const enabled =
    explicitEnabled ??
    (hasImageInput || isImageEvidenceVerificationShadowEnabled());

  if (!enabled || !sourceEvidence) return appSpec;

  const existingSurfaces = [
    ...(appSpec.generatedUiVerificationSurface
      ? [appSpec.generatedUiVerificationSurface]
      : []),
    ...(appSpec.generatedUiVerificationSurfaces ?? []),
  ];
  const hiddenTruths = options.inferredHiddenTruths ?? [];
  const generatedSurfaces =
    existingSurfaces.length > 0
      ? existingSurfaces.map((surface) => ({
          ...surface,
          sourceEvidence: surface.sourceEvidence ?? sourceEvidence,
        }))
      : appSpec.blocks.map((block, index) =>
          blockVerificationSurface(
            appSpec,
            block,
            index,
            sourceEvidence,
            hiddenTruths,
          ),
        );

  return {
    ...appSpec,
    imageEvidenceVerificationEnabled: true,
    imageSourceEvidence: sourceEvidence,
    generatedUiVerificationSurfaces: generatedSurfaces,
  };
}
